import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {catchError, shareReplay} from 'rxjs/operators';

import {SERVER_API_URL} from 'app/app.constants';

/** The password rules a tenant applies, as returned by the API. */
export interface PasswordRules {
    enforced: boolean;
    minLength: number;
    maxLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireNumber: boolean;
    requireSpecialCharacter: boolean;
}

/** Rules that let anything through, used when the tenant has not opted in or is unknown. */
export const NO_PASSWORD_RULES: PasswordRules = {
    enforced: false,
    minLength: 4,
    maxLength: 100,
    requireUppercase: false,
    requireLowercase: false,
    requireNumber: false,
    requireSpecialCharacter: false
};

@Injectable({providedIn: 'root'})
export class PasswordPolicyService {
    private cachedByTenant = new Map<string, Observable<PasswordRules>>();

    constructor(private http: HttpClient) {}

    /**
     * Fetches a tenant's password rules. Public on the API, because the pages that need them - the
     * reset link and the invitation form - have no session.
     * <p>
     * A failure resolves to "no rules": the API is the authority and will reject a bad password
     * anyway, so a policy we could not load must not block someone from typing one.
     */
    getRules(tenant: string): Observable<PasswordRules> {
        if (!tenant) {
            return of(NO_PASSWORD_RULES);
        }

        if (!this.cachedByTenant.has(tenant)) {
            const request = this.http.get<PasswordRules>(`${SERVER_API_URL}api/public/password-policy`, {params: {tenant}}).pipe(
                catchError(() => of(NO_PASSWORD_RULES)),
                shareReplay(1)
            );
            this.cachedByTenant.set(tenant, request);
        }

        return this.cachedByTenant.get(tenant);
    }
}
